import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  viewChild
} from '@angular/core';
import {FormControl, ReactiveFormsModule, ValidatorFn, Validators} from '@angular/forms';
import {FieldBlock} from '../../../shared/models/form-schema.model';
import {NgSelectModule} from '@ng-select/ng-select';
import {MaskedFieldsState} from '../../services/masked-fields-state.service';
import {SelectedFilesState} from '../../services/selected-files-state.service';
import {SERVER_ERROR_KEY} from '../../../shared/services/submission-errors';
import {
  attachmentTooLargeMessage,
  FILE_TOO_LARGE_KEY,
  isAttachmentTooLarge
} from '../../../shared/services/attachment-limits';

/** Cancels a touch's default action (scrolling, zooming) on the signature canvas. See the constructor (#879). */
function preventTouchDefault(event: Event): void {
  if (event.cancelable) event.preventDefault();
}

@Component({
  selector: 'forms-field-renderer',
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule],
  template: `
    <div class="field-container">
      @if (block.label) {
        <label [for]="block.id" class="block text-sm font-bold text-gray-700 mb-2 tracking-tight">
          {{ block.label }}
          @if (showRequired()) {
            <span class="text-red-500">*</span>
          }
        </label>
      }

      <div class="field-control relative">
        @if (isMaskGated() && !maskEditing) {
          <!-- A masked sensitive value carried into the revision: show it read-only; editing is deliberate. -->
          <div class="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 shadow-sm">
            <span class="truncate font-mono text-sm text-gray-600">{{ maskedValue }}</span>
            <button type="button" (click)="beginMaskEdit()"
                    class="inline-flex shrink-0 items-center gap-1 rounded-lg bg-theme-primary-50 px-2.5 py-1.5 text-xs font-bold text-theme-primary-600 transition-colors hover:bg-theme-primary-100">
              <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              Bearbeiten
            </button>
          </div>
        } @else {
          @if (isMaskGated()) {
            <button type="button" (click)="cancelMaskEdit()"
                    class="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-gray-600">
              <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 19l-7-7m0 0l7-7m-7 7h18" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              Maskierten Wert behalten
            </button>
          }
          @switch (block.type) {
            <!-- Single-line text variants -->
            @case ('text') {
              <input [formControl]="control" [id]="block.id" type="text"
                     [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300"/>
            }
            @case ('email') {
              <input [formControl]="control" [id]="block.id" type="email" inputmode="email"
                     [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300"/>
            }
            @case ('phone') {
              <input [formControl]="control" [id]="block.id" type="tel" inputmode="tel"
                     [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300"/>
            }

            <!-- Sensitive semantic types (encrypted at rest); rendered as text, monospace for codes -->
            @case ('iban') {
              <input [formControl]="control" [id]="block.id" type="text"
                     [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300 font-mono"/>
            }
            @case ('bank') {
              <input [formControl]="control" [id]="block.id" type="text"
                     [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300 font-mono"/>
            }
            @case ('idnumber') {
              <input [formControl]="control" [id]="block.id" type="text"
                     [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300"/>
            }
            @case ('taxid') {
              <input [formControl]="control" [id]="block.id" type="text" inputmode="numeric"
                     [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300 font-mono"/>
            }
            @case ('socialsecurity') {
              <input [formControl]="control" [id]="block.id" type="text"
                     [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300 font-mono"/>
            }
            @case ('textarea') {
              <textarea [formControl]="control" [id]="block.id" rows="4"
                        [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                        class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300 resize-y"></textarea>
            }
            @case ('number') {
              <input [formControl]="control" [id]="block.id" type="number" inputmode="decimal"
                     [placeholder]="block.placeholder || ''" [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200 placeholder-gray-300"/>
            }

            <!-- Select / multiselect -->
            @case ('select') {
              <ng-select [formControl]="control" [items]="block.options || []"
                         bindLabel="label" bindValue="value" [placeholder]="block.placeholder || ''"
                         class="custom-apple-select" [clearable]="!showRequired()"></ng-select>
            }
            @case ('multiselect') {
              <ng-select [formControl]="control" [items]="block.options || []"
                         bindLabel="label" bindValue="value" [multiple]="true" [closeOnSelect]="false"
                         [placeholder]="block.placeholder || ''" class="custom-apple-select"></ng-select>
            }

            <!-- Radio group -->
            @case ('radio') {
              <div class="space-y-2" role="radiogroup" [attr.aria-label]="block.label">
                @for (opt of block.options || []; track opt.value) {
                  <label class="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-all"
                         [class.border-theme-primary-400]="control.value === opt.value"
                         [class.bg-theme-primary-50]="control.value === opt.value">
                    <input type="radio" [value]="opt.value" [checked]="control.value === opt.value"
                           (change)="control.setValue(opt.value); control.markAsTouched()"
                           class="text-theme-primary-600 focus:ring-theme-primary-500"/>
                    <span class="text-sm font-medium text-gray-700">{{ opt.label }}</span>
                  </label>
                }
              </div>
            }

            <!-- Checkbox group (array value) -->
            @case ('checkbox-group') {
              <div class="space-y-2">
                @for (opt of block.options || []; track opt.value) {
                  <label class="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-all"
                         [class.border-theme-primary-400]="isChecked(opt.value)"
                         [class.bg-theme-primary-50]="isChecked(opt.value)">
                    <input type="checkbox" [checked]="isChecked(opt.value)" (change)="toggleCheckbox(opt.value)"
                           class="rounded text-theme-primary-600 focus:ring-theme-primary-500"/>
                    <span class="text-sm font-medium text-gray-700">{{ opt.label }}</span>
                  </label>
                }
              </div>
            }

            <!-- Date / time -->
            @case ('date') {
              <input [formControl]="control" [id]="block.id" type="date"
                     [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200"/>
            }
            @case ('time') {
              <input [formControl]="control" [id]="block.id" type="time"
                     [attr.aria-required]="showRequired()"
                     class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm transition-all duration-200"/>
            }

            <!-- Boolean toggle -->
            @case ('boolean') {
              <div class="flex items-center group cursor-pointer h-[42px]"
                   role="switch" [attr.aria-checked]="!!control.value" tabindex="0"
                   (click)="control.setValue(!control.value)"
                   (keydown.enter)="control.setValue(!control.value)" (keydown.space)="control.setValue(!control.value)">
                <div class="w-10 h-6 flex items-center bg-gray-200 rounded-full p-1 duration-300 ease-in-out transition-colors"
                     [class.bg-theme-primary-500]="control.value">
                  <div class="bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out"
                       [class.translate-x-4]="control.value"></div>
                </div>
                <span class="ml-3 text-sm text-gray-700 font-bold tracking-tight">{{ block.placeholder || 'Aktivieren' }}</span>
              </div>
            }

            <!-- File upload. The control carries the file NAME (for validators + the review page); the File
                 itself is kept alongside so the submit can actually send the bytes (#258). -->
            @case ('file') {
              <div>
                <input #fileInput type="file" [id]="block.id" [multiple]="!!block.multiple"
                       (change)="onFileChange($event)" [attr.aria-required]="showRequired()"
                       class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-theme-primary-50 file:text-theme-primary-600 hover:file:bg-theme-primary-100 cursor-pointer"/>
                @if (control.value) {
                  <div class="mt-2 flex items-center gap-2">
                    <p class="min-w-0 flex-1 truncate text-xs font-medium text-gray-500" data-test="file-label">
                      {{ fileLabel() }}
                    </p>
                    <button type="button" (click)="clearFile()" data-test="file-clear"
                            [attr.aria-label]="'Datei entfernen: ' + fileLabel()"
                            class="shrink-0 text-xs font-bold text-gray-600 transition-colors hover:text-red-600">
                      Entfernen
                    </button>
                  </div>
                }
              </div>
            }

            <!-- Signature pad -->
            @case ('signature') {
              <div class="space-y-2">
                <!-- No pointerleave handler on purpose: the pointer is captured, so a stroke that runs past the
                     edge stays one stroke and ends on pointerup or pointercancel (#879). -->
                <canvas #sigCanvas width="600" height="180"
                        class="w-full h-44 bg-gray-50 border border-gray-200 rounded-xl touch-none overscroll-contain cursor-crosshair"
                        (pointerdown)="startDraw($event)" (pointermove)="draw($event)"
                        (pointerup)="endDraw()" (pointercancel)="endDraw()"></canvas>
                <button type="button" (click)="clearSignature()"
                        class="text-xs font-bold text-gray-500 hover:text-red-500 transition-colors">Löschen</button>
              </div>
            }
          }
        }
      </div>

      <!-- Validation Errors -->
      @if (control.invalid && (control.dirty || control.touched)) {
        <div class="mt-2 flex items-center space-x-1 text-xs font-semibold text-red-600 animate-in fade-in slide-in-from-top-1 duration-200"
             data-test="field-error" role="alert">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"/>
          </svg>
          <span>{{ errorText() }}</span>
        </div>
      }
    </div>
  `
})
export class FieldRendererComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() block!: FieldBlock;
  @Input() control!: FormControl;

  /**
   * The signature canvas, as a signal. It lives inside `@case ('signature')` and behind the mask gate, so it can
   * appear (or be replaced) well after the first view init; the touch guard in the constructor follows it.
   */
  private readonly sigCanvasRef = viewChild<ElementRef<HTMLCanvasElement>>('sigCanvas');
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  private drawing = false;

  /** The signature canvas currently rendered, or `undefined` when this field shows none. */
  get sigCanvas(): ElementRef<HTMLCanvasElement> | undefined {
    return this.sigCanvasRef();
  }

  constructor() {
    // Blocks page scrolling for touches that start on the signature canvas (#879).
    //
    // The canvas already carries `touch-action: none`, but iOS Safari does not reliably honour it: it claims the
    // finger as a scroll, fires `pointercancel` and moves the page under the stroke. Cancelling `touchstart` and
    // `touchmove` stops that. The listeners must be registered with `passive: false`, because browsers treat
    // touch listeners as passive by default and ignore `preventDefault()` inside them; an Angular `(touchmove)`
    // template binding cannot set that option, hence `addEventListener`. Only the canvas is guarded, so the
    // rest of the form still scrolls.
    effect(onCleanup => {
      const canvas = this.sigCanvasRef()?.nativeElement;
      if (!canvas) return;
      const options: AddEventListenerOptions = {passive: false};
      canvas.addEventListener('touchstart', preventTouchDefault, options);
      canvas.addEventListener('touchmove', preventTouchDefault, options);
      // Runs when the canvas goes away or is replaced, and when the component is destroyed.
      onCleanup(() => {
        canvas.removeEventListener('touchstart', preventTouchDefault, options);
        canvas.removeEventListener('touchmove', preventTouchDefault, options);
      });
    });
  }

  /** Present only in the revision editor; tells us which fields carry a masked stored value. */
  private readonly maskState = inject(MaskedFieldsState, {optional: true});
  /**
   * Present on screens that submit attachments; absent in the builder preview. The picked {@link File} goes here
   * so the submit can send the actual bytes — the control itself can only hold the file name (#258).
   */
  private readonly fileState = inject(SelectedFilesState, {optional: true});
  /** The files picked in this renderer, kept so the bytes survive from "picked" to "submitted". */
  private pickedFiles: File[] = [];
  /** The masked stored value, shown read-only until the employee chooses to edit. */
  protected maskedValue: unknown = null;
  /** True once the employee has clicked "Bearbeiten" on a masked field — it then renders the normal input. */
  protected maskEditing = false;

  /**
   * The message for a pick this renderer refused as too large (#779), or `null` while nothing is refused.
   *
   * Held on the component rather than written straight onto the control because {@link oversizeValidator} reads it
   * — see there for why a validator and not `setErrors`.
   */
  private oversizedPick: string | null = null;

  /**
   * Keeps the control invalid for as long as the last pick stands refused, so the submit gate
   * (`DynamicFormComponent.onSubmit` → `form.valid`) blocks and the field is named in the "why can't I submit"
   * summary.
   *
   * A **validator**, not `control.setErrors(...)`, for the reason `stampServerError` documents in
   * `submission-errors.ts`: the `formControl` directive re-validates a control whenever its view is
   * (re-)initialised, and any error written directly is wiped by that — which is exactly what a multi-page
   * wizard does every time it moves between pages. As a validator the refusal survives the re-render, and it
   * clears itself the moment the next pick resolves {@link oversizedPick} back to `null`.
   */
  private readonly oversizeValidator: ValidatorFn = () =>
    this.oversizedPick === null ? null : {[FILE_TOO_LARGE_KEY]: this.oversizedPick};

  ngOnInit() {
    // Capture the carried mask so we can display it and restore it on "keep". The form is built + patched
    // before child renderers init, so the control already holds the mask string here.
    if (this.maskState?.isMasked(this.block.key)) {
      this.maskedValue = this.control.value;
    }

    // Attached up front, and inert until a pick is refused: adding it only on refusal would mean adding a
    // validator from inside the change handler, where the control is mid-update.
    if (this.block.type === 'file') this.control.addValidators(this.oversizeValidator);
  }

  ngOnDestroy() {
    // The refusal is this renderer's state. Leaving the validator behind would hold a control invalid with no
    // field on screen to fix it — and nothing to fix, since the refused pick was never retained.
    if (this.block?.type === 'file' && this.control) {
      this.control.removeValidators(this.oversizeValidator);
      this.control.updateValueAndValidity({emitEvent: false});
    }
  }

  ngAfterViewInit() {
    if (this.block.type === 'signature' && this.control.value && this.sigCanvas) {
      const img = new Image();
      const ctx = this.sigCanvas.nativeElement.getContext('2d');
      img.onload = () => ctx?.drawImage(img, 0, 0);
      img.src = this.control.value;
    }
  }

  showRequired(): boolean {
    return this.block.required === true || this.control?.hasValidator(Validators.required);
  }

  // --- masked-value edit gate (revision editor) ------------------------------
  /** True when this field carries a masked stored value (so it's shown read-only with an edit affordance). */
  isMaskGated(): boolean {
    return this.maskedValue !== null && !!this.maskState?.isMasked(this.block.key);
  }

  /**
   * Reveal the input for a deliberate edit, starting from empty so the mask isn't mistaken for the real value.
   *
   * Resets to the type's **empty** value, never to the field's default (#388): clearing is an explicit user
   * action meaning "this is now blank", and a stored value being replaced by a form-author's default would be a
   * silent data change. Same intent as {@link clearFile} / {@link clearSignature} — do not "fix" this to call
   * `resolveDefault`.
   */
  beginMaskEdit(): void {
    this.maskEditing = true;
    this.control.setValue(this.block.type === 'boolean' ? false : '');
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  /** Revert to the masked value — the field is then sent as `keep` (the stored value is preserved untouched). */
  cancelMaskEdit(): void {
    this.maskEditing = false;
    this.control.setValue(this.maskedValue);
    this.control.markAsPristine();
  }

  // --- checkbox-group helpers ------------------------------------------------
  isChecked(value: any): boolean {
    return Array.isArray(this.control.value) && this.control.value.includes(value);
  }

  toggleCheckbox(value: any) {
    const current: any[] = Array.isArray(this.control.value) ? [...this.control.value] : [];
    const idx = current.indexOf(value);
    if (idx === -1) current.push(value); else current.splice(idx, 1);
    this.control.setValue(current);
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  // --- file ------------------------------------------------------------------
  /**
   * Records a file pick. The control keeps the file **name** (that is what the validators, the rules engine and
   * the review page work with, and it is all that can survive `getRawValue()`), while the `File` itself is
   * retained here and published to {@link SelectedFilesState} so the submitting screen can send the bytes.
   *
   * Until #258 only the name was kept, which is why a picked file looked attached and never arrived.
   *
   * A file over {@link MAX_ATTACHMENT_BYTES} is refused here and now (#779) rather than at submit time: the edge
   * proxy answers an oversized multipart with a `413` the browser cannot read (no CORS header on it), so the
   * employee used to get a generic "konnte nicht gesendet werden" banner with nothing to act on. See
   * {@link rejectOversizedPick} for what a refusal does and why.
   */
  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || files.length === 0) {
      this.oversizedPick = null;
      this.pickedFiles = [];
      this.fileState?.clear(this.block.key);
      this.control.setValue('');
    } else {
      const picked = Array.from(files);
      // The WHOLE pick is refused when ANY file in it is too large, even though only the first one is ever
      // uploaded. Taking the rest would quietly swap in a different document than the one the employee picked
      // — the same invisible mismatch #258 was about — and a partial accept has no honest label to show.
      const oversized = picked.find(isAttachmentTooLarge);
      if (oversized) {
        this.rejectOversizedPick(input, oversized);
        return;
      }

      this.oversizedPick = null;
      this.pickedFiles = picked;
      // One attachment per field is what the backend stores (replace semantics), so a multi-pick still publishes
      // the first file; the label shows every name so the mismatch is visible rather than silent.
      this.fileState?.set(this.block.key, this.pickedFiles[0]);
      this.control.setValue(this.block.multiple
        ? this.pickedFiles.map(f => f.name)
        : this.pickedFiles[0].name);
    }

    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  /**
   * Refuses a pick that carries an oversized file: nothing is retained, nothing is published to
   * {@link SelectedFilesState}, the control value goes back to empty and {@link oversizeValidator} takes over so
   * the field stays invalid with its own message until the employee picks something else.
   *
   * It drops a *previously* accepted file too. The native input has already replaced its own selection with the
   * new pick, so keeping the old bytes would leave the field claiming one file and submitting another.
   *
   * **The native input is reset.** Without that, the input still holds the refused file, and a file input fires
   * no `change` event when the same file is chosen again — so an employee who shrinks the document and picks the
   * very same path would be stuck looking at an error nothing could clear.
   */
  private rejectOversizedPick(input: HTMLInputElement, offender: File): void {
    this.oversizedPick = attachmentTooLargeMessage(offender);
    this.pickedFiles = [];
    this.fileState?.clear(this.block.key);
    this.control.setValue('');          // re-runs the validators, so the refusal becomes the control's error
    this.control.markAsDirty();
    this.control.markAsTouched();
    input.value = '';
  }

  /** The files currently held for this field (empty when nothing is picked). */
  selectedFiles(): File[] {
    return this.pickedFiles;
  }

  /** The single file that would be uploaded for this field, or null. */
  selectedFile(): File | null {
    return this.pickedFiles[0] ?? null;
  }

  /**
   * Drops the pick — clears the retained bytes, the control value and the native input. Clearing always lands on
   * empty, never on a default (#388); `file` carries no default anyway (DEFAULT_VALUE_UNSUPPORTED_TYPES).
   */
  clearFile(): void {
    this.oversizedPick = null;          // an explicit clear also withdraws a standing size refusal (#779)
    this.pickedFiles = [];
    this.fileState?.clear(this.block.key);
    this.control.setValue('');
    this.control.markAsDirty();
    this.control.markAsTouched();
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  fileLabel(): string {
    const v = this.control.value;
    return Array.isArray(v) ? v.join(', ') : String(v ?? '');
  }

  // --- signature -------------------------------------------------------------
  /**
   * Starts a stroke and captures the pointer to the canvas (#879).
   *
   * With the capture, every further move, `pointerup` and `pointercancel` of this pointer reaches the canvas even
   * after the finger or mouse leaves it, so a signature that runs past the edge stays one stroke instead of being
   * cut off and saved half-drawn.
   */
  startDraw(event: PointerEvent) {
    if (!this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;
    try {
      canvas.setPointerCapture?.(event.pointerId);
    } catch {
      // The pointer is no longer active (it was released between dispatch and handling). Drawing still works
      // without the capture, it just ends at the edge.
    }
    this.drawing = true;
    const ctx = this.ctx();
    if (!ctx) return;
    const {x, y} = this.point(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  draw(event: PointerEvent) {
    if (!this.drawing) return;
    const ctx = this.ctx();
    if (!ctx) return;
    const {x, y} = this.point(event);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111827';
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  /**
   * Ends the stroke and stores the canvas as a PNG data URL. Bound to `pointerup` and to `pointercancel`: a
   * cancelled pointer (the browser took over the gesture, or the tab lost focus mid-stroke) keeps what was drawn
   * rather than dropping it (#879).
   */
  endDraw() {
    if (!this.drawing || !this.sigCanvas) return;
    this.drawing = false;
    this.control.setValue(this.sigCanvas.nativeElement.toDataURL('image/png'));
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  clearSignature() {
    const ctx = this.ctx();
    if (ctx && this.sigCanvas) {
      ctx.clearRect(0, 0, this.sigCanvas.nativeElement.width, this.sigCanvas.nativeElement.height);
    }
    this.control.setValue('');
  }

  private ctx(): CanvasRenderingContext2D | null {
    return this.sigCanvas?.nativeElement.getContext('2d') ?? null;
  }

  private point(event: PointerEvent): { x: number; y: number } {
    const canvas = this.sigCanvas!.nativeElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height)
    };
  }

  // --- error message ---------------------------------------------------------
  errorText(): string {
    const e = this.control.errors;
    if (!e) return '';
    const m = this.block.validators?.messages || {};
    // A rejection the SERVER reported for this field wins: it is the authority that refused the submit, its
    // message is already localised, and showing it here is what turns a 422 from "stuck" into "fixable" (#258).
    if (e[SERVER_ERROR_KEY]) return String(e[SERVER_ERROR_KEY]);
    // Ahead of `required` on purpose (#779): a refused pick leaves the value empty, so a required file field
    // carries both errors — and "Dieses Feld ist ein Pflichtfeld." would hide the only fact that explains it.
    if (e[FILE_TOO_LARGE_KEY]) return String(e[FILE_TOO_LARGE_KEY]);
    if (e['required']) return m['required'] || 'Dieses Feld ist ein Pflichtfeld.';
    if (e['email']) return m['email'] || 'Ungültige E-Mail-Adresse.';
    if (e['min']) return m['min'] || `Mindestwert ist ${e['min'].min}.`;
    if (e['max']) return m['max'] || `Maximalwert ist ${e['max'].max}.`;
    if (e['minlength']) return m['minlength'] || `Mindestens ${e['minlength'].requiredLength} Zeichen erforderlich.`;
    if (e['maxlength']) return m['maxlength'] || `Höchstens ${e['maxlength'].requiredLength} Zeichen erlaubt.`;
    if (e['pattern']) return m['pattern'] || 'Ungültiges Format.';
    if (e['minDate']) return m['minDate'] || `Mindestdatum ist ${e['minDate'].minDate}.`;
    if (e['maxDate']) return m['maxDate'] || `Maximaldatum ist ${e['maxDate'].maxDate}.`;
    return 'Ungültige Eingabe.';
  }
}
